<template>
  <nav
    v-if="totalPages > 1"
    class="-mb-1 mt-6 min-w-0"
    aria-label="Paginacja"
  >
    <!-- Mobile: prev/next + status only -->
    <div class="flex min-w-0 items-center justify-between gap-3 lg:hidden">
      <component
        :is="previousLink.url ? 'Link' : 'span'"
        v-bind="previousLink.url ? { href: previousLink.url, preserveState: true, preserveScroll: true } : {}"
        :class="navButtonClass(!previousLink.url, true)"
        :aria-disabled="!previousLink.url ? true : undefined"
      >
        <svg class="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        <span class="sr-only">{{ previousLink.label }}</span>
      </component>

      <p class="min-w-0 text-center text-sm font-medium text-neutral-600">
        Strona {{ currentPage }} z {{ totalPages }}
      </p>

      <component
        :is="nextLink.url ? 'Link' : 'span'"
        v-bind="nextLink.url ? { href: nextLink.url, preserveState: true, preserveScroll: true } : {}"
        :class="navButtonClass(!nextLink.url, true)"
        :aria-disabled="!nextLink.url ? true : undefined"
      >
        <svg class="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
        <span class="sr-only">{{ nextLink.label }}</span>
      </component>
    </div>

    <!-- Desktop -->
    <div class="hidden flex-wrap lg:flex">
      <template v-for="(link, key) in links">
        <div
          v-if="link.label === 'gap'"
          :key="key"
          class="mb-1 mr-1 px-4 py-3 text-sm text-gray-500"
        >
          …
        </div>
        <div
          v-else-if="link.url === null"
          :key="`${key}-disabled`"
          class="mb-1 mr-1 rounded-sm border border-gray-400 px-4 py-3 text-sm text-gray-700"
          :class="{ 'ml-auto': link.label === paginationNextLabel }"
        >
          {{ link.label }}
        </div>
        <Link
          v-else
          :key="`${key}-link`"
          class="mb-1 mr-1 rounded-sm border border-gray-400 px-4 py-3 text-sm text-gray-900 hover:bg-white focus:border-indigo-500 focus:text-indigo-500"
          :class="{ 'bg-white': link.active, 'ml-auto': link.label === paginationNextLabel }"
          :href="link.url"
          preserve-state
          preserve-scroll
        >
          {{ link.label }}
        </Link>
      </template>
    </div>
  </nav>
</template>

<script>
import { Link } from '@inertiajs/vue3';

const PAGINATION_LABEL_PREVIOUS = 'Poprzednia';
const PAGINATION_LABEL_NEXT = 'Następna';

export default {
  components: {
    Link,
  },
  props: {
    meta: {
      type: Object,
      required: true,
    },
  },

  computed: {
    paginationNextLabel() {
      return PAGINATION_LABEL_NEXT;
    },
    currentPage() {
      const page = this.meta.page;
      return typeof page === 'string' ? parseInt(page, 10) : page;
    },
    totalPages() {
      const last = this.meta.last;
      if (last == null) return 1;
      return typeof last === 'string' ? parseInt(last, 10) : last;
    },
    links() {
      const series = this.meta.series || [];
      return [
        {
          label: PAGINATION_LABEL_PREVIOUS,
          url: this.url(this.meta.previous),
        },
        ...series.map((page) => {
          return {
            label: page === 'gap' ? 'gap' : page,
            url: page === 'gap' ? null : this.url(page),
            active: this.active(page),
          };
        }),
        {
          label: PAGINATION_LABEL_NEXT,
          url: this.url(this.meta.next),
        },
      ];
    },
    previousLink() {
      return this.links[0];
    },
    nextLink() {
      return this.links[this.links.length - 1];
    },
  },

  methods: {
    url(pageNumber) {
      if (!pageNumber || pageNumber === 'gap') {
        return null;
      }
      const num = typeof pageNumber === 'string' ? parseInt(pageNumber, 10) : pageNumber;
      if (isNaN(num)) {
        return null;
      }
      return this.meta.url_template.replace('P ', num);
    },

    active(pageNumber) {
      const num = typeof pageNumber === 'string' ? parseInt(pageNumber, 10) : pageNumber;
      return this.meta.page == num;
    },

    navButtonClass(disabled, compact = false) {
      return [
        'flex items-center justify-center gap-1 rounded border text-sm transition-colors',
        compact ? 'size-10 shrink-0 p-0' : 'px-3 py-2',
        disabled
          ? 'cursor-not-allowed border-neutral-100 bg-white text-neutral-300'
          : 'border-neutral-100 bg-white text-neutral-700 hover:border-neutral-200 no-underline',
      ];
    },
  },
};
</script>
